import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText } from 'lucide-react';

// Rendu des réponses de Nexus et de Muse : typographie lisible sans plugin Tailwind, gras surligné (l'élément clé
// de la réponse), tableaux, code, et la ligne « Source : … » en note discrète.

const textOf = (children) => React.Children.toArray(children)
  .map((child) => (typeof child === 'string' ? child : textOf(child?.props?.children)))
  .join('');

const SOURCE_RE = /^\s*sources?\s*:/i;

const components = {
  p: ({ children }) => {
    if (SOURCE_RE.test(textOf(children))) {
      return (
        <p className="mt-3 flex items-start gap-1.5 border-t border-slate-100 pt-2 text-xs leading-relaxed text-slate-500">
          <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
          <span>{children}</span>
        </p>
      );
    }
    return <p className="my-2 leading-relaxed first:mt-0 last:mb-0">{children}</p>;
  },
  strong: ({ children }) => (
    <strong className="rounded-sm bg-[linear-gradient(transparent_58%,rgba(253,230,138,0.9)_58%)] px-0.5 font-semibold text-slate-950">
      {children}
    </strong>
  ),
  em: ({ children }) => <em className="italic text-slate-700">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="font-medium text-indigo-600 underline decoration-indigo-200 underline-offset-2 hover:decoration-indigo-500">
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5 marker:text-indigo-300">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5 marker:text-indigo-400">{children}</ol>,
  li: ({ children }) => <li className="pl-0.5 leading-relaxed">{children}</li>,
  h1: ({ children }) => <h3 className="mb-1 mt-4 text-base font-semibold text-slate-900 first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="mb-1 mt-4 text-[0.95rem] font-semibold text-slate-900 first:mt-0">{children}</h3>,
  h3: ({ children }) => <h4 className="mb-1 mt-3 text-sm font-semibold text-slate-900 first:mt-0">{children}</h4>,
  h4: ({ children }) => <h4 className="mb-1 mt-3 text-sm font-semibold text-slate-700 first:mt-0">{children}</h4>,
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-4 border-indigo-200 bg-indigo-50/40 py-1 pl-3 text-slate-700">{children}</blockquote>
  ),
  hr: () => <hr className="my-4 border-slate-200" />,
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-xl bg-slate-900 p-3 text-xs leading-relaxed text-slate-100">{children}</pre>
  ),
  code: ({ className, children }) => {
    const block = /language-/.test(className || '') || String(children).includes('\n');
    if (block) return <code className={`font-mono ${className || ''}`}>{children}</code>;
    return <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-slate-800">{children}</code>;
  },
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full border-collapse text-left text-[0.85rem]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50 text-slate-700">{children}</thead>,
  // style : alignement des colonnes (« ---: » dans le tableau Markdown).
  th: ({ children, style }) => (
    <th style={style} className="whitespace-nowrap border-b border-slate-200 px-3 py-2 font-semibold">{children}</th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="border-b border-slate-100 px-3 py-2 align-top tabular-nums">{children}</td>
  ),
  tr: ({ children }) => <tr className="last:[&>td]:border-b-0">{children}</tr>,
};

const Markdown = ({ children, className = '' }) => (
  <div className={`break-words text-[0.92rem] text-slate-800 ${className}`}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{children || ''}</ReactMarkdown>
  </div>
);

Markdown.propTypes = {
  children: PropTypes.string,
  className: PropTypes.string,
};

export default Markdown;
